import type { Building } from "building-types/building/buildingTypes";
import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import { builtInPropAttributes } from "doc/elements/propCatalog/builtInProps";
import { resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { flattenSettingsSets } from "doc/generate/flattenSettingsSets";
import { getActiveElevationId } from "doc/options/activeElevation";
import { docGetElevationOptionSetOrThrow } from "doc/options/docGetOptionSets";
import { defaultHomeSettings } from "doc/settings/defaultSettings";
import settingsLibrary from "doc/settingsLibrary";
import { finalizeSettings } from "../generationTools/generateSettingsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: {
    optionSets,
    instanceOptionSets,
    settingsSets,
    settings,
    propAttributes,
    sheathingComponents,
    framingComponents,
    masonryComponents,
    cipComponents,
    railingCatalog,
  },
  optionChoices,
  entityDisabledByGroup,
  enabledFeatures,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const activeElevationId = getActiveElevationId(optionSets, optionChoices);
  const elevationOptionSet = docGetElevationOptionSetOrThrow({ optionSets });

  return {
    optionChoices: resolveOptionsInFormula(settingsSets, definitions, optionChoices),
    settings,
    elevationBaseSettings: activeElevationId ? elevationOptionSet.baseSettings[activeElevationId] : undefined,
    settingsSets,
    optionSets,
    entityDisabledByGroup,
    propAttributes,
    sheathingComponents,
    framingComponents,
    masonryComponents,
    cipComponents,
    railingCatalog,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  entityDisabledByGroup: "shallow",
  enabledFeatures: "shallow",
});

function generate(props: ReturnType<typeof select>) {
  const {
    elevationBaseSettings,
    propAttributes,
    sheathingComponents,
    framingComponents,
    masonryComponents,
    cipComponents,
    railingCatalog,
    enabledFeatures,
  } = props;
  const { mergedSettings, activeSettingsSets } = flattenSettingsSets(
    {
      ...defaultHomeSettings,
      ...(elevationBaseSettings !== undefined ? settingsLibrary[elevationBaseSettings] : undefined),
    } as HomeSettingsRW,
    props,
  );

  if (elevationBaseSettings !== undefined) {
    const elevationSettings = settingsLibrary[elevationBaseSettings];
    mergedSettings.props = {
      ...builtInPropAttributes,
      ...elevationSettings?.interiorProps?.propTypes,
      ...elevationSettings?.exteriorProps?.propTypes,
      ...propAttributes,
    };
  } else {
    mergedSettings.props = { ...builtInPropAttributes, ...propAttributes };
  }

  return {
    settingsSets: activeSettingsSets,
    settings: finalizeSettings(
      mergedSettings,
      sheathingComponents,
      framingComponents,
      masonryComponents,
      cipComponents,
      railingCatalog,
      enabledFeatures,
    ),
  };
}

export default memo(select, generate, compare);
